import { useTranslation } from "react-i18next";

import type { CcCorrectionAttempt } from "@/api/code-companion";
import { useCcCorrections } from "@/hooks/useCodeCompanion";
import { formatCost, formatDuration } from "@/lib/code-companion-session";
import { tp } from "@/lib/plural";

/**
 * A narrativa da autocorreção (S-064 onda 1).
 *
 * É o que o Code Companion faz de mais interessante e o que menos aparecia: o alvo
 * recusa o objeto, o modelo lê a reclamação, reescreve o código e o objeto passa. No
 * ledger de deploy isso deixava "deploy | false" numa rodada e "deploy | true" na
 * seguinte, sem nada no meio — na tela, um objeto que virava verde por conta própria.
 *
 * Cada tentativa mostra quatro coisas, nesta ordem, porque é a ordem em que aconteceram:
 * o que o alvo reclamou, a hipótese do modelo, o que ele mudou, e o desfecho.
 *
 * **`resolved` e `result` são diferentes.** `result: applied` diz que o modelo produziu
 * código novo; `resolved` diz que a rodada **seguinte** não reclamou mais. Uma tentativa
 * pode ser aplicada e não resolver, e é justamente esse caso que interessa ler.
 *
 * **Custo `null` não é zero.** `null` é "não chamou o modelo"; zero seria "chamou de
 * graça". A tela não pode achatar os dois.
 */

/** Desfecho da tentativa, que é o que decide a cor da linha. */
export function attemptTone(
  a: CcCorrectionAttempt,
): "resolved" | "escalated" | "refused" | "open" {
  if (a.resolved) return "resolved";
  if (a.escalated) return "escalated";
  if (a.result === "refused") return "refused";
  return "open";
}

const TONE: Record<string, string> = {
  resolved: "#2b8f57",
  escalated: "#b02a31",
  refused: "#b45309",
  open: "#6b7280",
};

interface Props {
  projectId: string;
  changeSetId: number;
}

export default function CorrectionTrail({ projectId, changeSetId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCcCorrections(projectId, changeSetId);

  if (isError) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Could not read the correction trail.")}
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Loading correction trail...")}
      </div>
    );
  }

  // Correção é excepcional: nenhuma tentativa é o caso bom, e dizer isso é melhor que
  // uma caixa vazia que parece defeito de carregamento.
  if (!data.objects.length) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("No object needed correction in this change set.")}
      </div>
    );
  }

  return (
    <div
      data-testid="correction-trail"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Correction trail")}
        </span>
        {data.any_escalated && (
          <span
            data-testid="correction-needs-human"
            className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
            style={{ background: "#fee2e2", color: TONE.escalated }}
          >
            {t("needs a human")}
          </span>
        )}
        <div className="flex-1" />
        {formatCost(data.cost_usd) && (
          <span
            data-testid="correction-cost"
            className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {t("model cost")} {formatCost(data.cost_usd)}
          </span>
        )}
      </div>

      <ul className="divide-y divide-border">
        {data.objects.map((obj) => (
          <li key={obj.object_name} className="px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[11px] font-semibold">
                {obj.object_name}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {/* A contagem é do change set, somando execuções anteriores (#603). */}
                {tp(t, obj.attempts, "1 attempt in this change set", "{{n}} attempts in this change set")}
              </span>
              <div className="flex-1" />
              {formatCost(obj.cost_usd) && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {formatCost(obj.cost_usd)}
                </span>
              )}
            </div>

            <ol className="mt-1 space-y-1.5">
              {obj.history.map((a) => (
                <li
                  key={a.attempt_no}
                  className="border-l-2 pl-2"
                  style={{ borderColor: TONE[attemptTone(a)] }}
                >
                  <div className="flex flex-wrap items-baseline gap-2 text-[10px]">
                    <span
                      className="font-mono font-semibold uppercase"
                      style={{ color: TONE[attemptTone(a)] }}
                    >
                      {t("attempt {{n}}", { n: a.attempt_no })}
                    </span>
                    {a.error_class && (
                      <span className="font-mono text-muted-foreground">
                        {a.error_class}
                      </span>
                    )}
                    {/* Só quando NÃO foi o run: autônomo é o caso esperado nesta tela,
                        e carimbar todas as linhas com o óbvio esconde a exceção. */}
                    {a.actor_type && a.actor_type !== "autonomous" && (
                      <span
                        data-testid="attempt-actor"
                        className="rounded bg-muted px-1 font-mono text-muted-foreground"
                      >
                        {a.actor_type}
                      </span>
                    )}
                    <div className="flex-1" />
                    {formatDuration(a.duration_ms) && (
                      <span className="font-mono text-muted-foreground">
                        {formatDuration(a.duration_ms)}
                      </span>
                    )}
                  </div>

                  {a.sap_finding && (
                    <div className="mt-0.5 break-words text-[11px]">
                      <span className="text-muted-foreground">
                        {t("target said")}{" "}
                      </span>
                      <span className="font-mono">{a.sap_finding}</span>
                    </div>
                  )}
                  {a.hypothesis && (
                    <div className="mt-0.5 break-words text-[11px]">
                      <span className="text-muted-foreground">
                        {t("hypothesis")}{" "}
                      </span>
                      {a.hypothesis}
                    </div>
                  )}
                  {a.change_summary && (
                    <div className="mt-0.5 break-words text-[11px]">
                      <span className="text-muted-foreground">{t("change")} </span>
                      {a.change_summary}
                    </div>
                  )}
                  {/* Recusa não tem mudança a mostrar, e o silêncio pareceria dado
                      faltando em vez de decisão do modelo. */}
                  {a.result === "refused" && !a.change_summary && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {t("the model did not produce a fix")}
                    </div>
                  )}
                  {/* Os hashes são o par que prova que o código mudou. Truncados: o
                      valor aqui é conferir, não ler 64 hex. */}
                  {a.source_hash_before && a.source_hash_after && (
                    <div
                      className="mt-0.5 font-mono text-[10px] text-muted-foreground"
                      title={`${a.source_hash_before} → ${a.source_hash_after}`}
                    >
                      {a.source_hash_before.slice(0, 8)} →{" "}
                      {a.source_hash_after.slice(0, 8)}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ul>
    </div>
  );
}
